import React from 'react';

import { useAppSelector } from '@/store';

import { useFeatureGate } from '../../../hooks/useFeatureGate';
import { useBrandSettled } from '../../website/useBrandSettled';
import { creativesServiceConfigured } from '../creative-api';
import { resolveSiteUrl } from '../live-preview';
import { resolvePreviewDomain } from '../site-host';
import { getSitePagesShared } from '../site-pages-api';
import { warmSiteTranslations } from '../useSiteTranslations';

/** How long the warm-up waits for the browser to be idle before it starts anyway. A browser that cannot say when it is
 *  idle (no `requestIdleCallback`) waits this long too. */
export const WARMUP_IDLE_TIMEOUT_MS = 3_000;

/** The site's host as Game Lists sends it to the service: the preview host made a site URL, as the Creatives page builds
 *  Game Lists' `siteUrl` (the language it adds there is a query, never the host), then its host; '' when there is none. */
const siteHostOf = (previewDomain: string | undefined): string => {
  try {
    return new URL(resolveSiteUrl(previewDomain)).host;
  } catch {
    return '';
  }
};

/** Run `task` once the browser is idle, at the latest after `WARMUP_IDLE_TIMEOUT_MS`; returns its cancel. */
const whenIdle = (task: () => void): (() => void) => {
  if (typeof window.requestIdleCallback === 'function') {
    const handle = window.requestIdleCallback(task, { timeout: WARMUP_IDLE_TIMEOUT_MS });
    return () => window.cancelIdleCallback(handle);
  }
  const timer = window.setTimeout(task, WARMUP_IDLE_TIMEOUT_MS);
  return () => window.clearTimeout(timer);
};

/**
 * Warms what Game Lists opens with, from every page of the Studio (mounted once, in MainLayout): once the brand on screen
 * has settled, no brand switch is under way and the browser is idle, it looks the brand's internal host up
 * (`resolvePreviewDomain`, the lookup `useBrandSiteHost` shares) and reads its site's pages by the host Game Lists sends
 * (`getSitePagesShared`), then, when they name the site's language, the site's translation table Game Lists names the
 * pages in (`warmSiteTranslations`). Entering Website, then Game Lists, then finds every answer in.
 *
 * Once per brand: a re-render schedules nothing, the end of a brand switch schedules the new brand, and a warm-up that
 * has not started when the brand changes or the layout goes is dropped. Only with Game Lists on and the creatives
 * service configured. It renders nothing and never holds up a render, and nothing it does can fail the layout.
 */
export const useGameListsWarmup = (): void => {
  // The brand as the Creatives page hands it to Game Lists and `useBrandSiteHost`, so the answers warmed are the ones
  // they read: the casino instance (the site-pages key), the tenant (a positive number, else none) and the raw public
  // domain. Selected field by field, so the layout renders again only when one of them changes.
  const casinoInstanceId = useAppSelector((state) => state.casinoInstance.displayBrand?.id) || undefined;
  const tenantRaw = Number(useAppSelector((state) => state.casinoInstance.displayBrand?.tenant_id));
  const tenantId = Number.isFinite(tenantRaw) && tenantRaw > 0 ? tenantRaw : undefined;
  const publicDomain = useAppSelector((state) => state.casinoInstance.displayBrand?.casino_data?.details?.domain);
  const switching = useAppSelector((state) => state.casinoInstance.brandSwitchPhase !== 'idle');
  const brandSettled = useBrandSettled();
  const gamesOn = useFeatureGate().isEnabled('creatives-games');
  const ready = brandSettled && !switching && gamesOn;

  React.useEffect(() => {
    if (!ready || casinoInstanceId === undefined || !creativesServiceConfigured()) return undefined;
    let cancelled = false;
    const warm = async (): Promise<void> => {
      const host = siteHostOf(await resolvePreviewDomain(tenantId, publicDomain));
      if (cancelled || host === '') return;
      const pages = await getSitePagesShared(casinoInstanceId, host);
      if (!cancelled && pages.ok && pages.value.language) warmSiteTranslations(tenantId, pages.value.language);
    };
    const cancel = whenIdle(() => {
      // A failed read is not kept, so Game Lists simply reads again: nothing here reaches the layout.
      warm().catch(() => undefined);
    });
    return () => {
      cancelled = true;
      cancel();
    };
  }, [ready, casinoInstanceId, tenantId, publicDomain]);
};
